// per specs/060-agreed-alerts/plan.md C1, C4, tasks T021
// The watched group's page: name, description, the safety threshold A5 and A10 share, and the
// members. Composed from the admin form idiom (FormPage bands); there is no wireframe for it.
// The members are their own writes (WatchedGroupMembers), so a new group is saved first and
// the members are added on the page it lands on.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudInfoBox,
  MudSelect,
  MudSwitch,
  MudTextInput,
  MudTextarea,
} from '@egov-moldova/mud-react';
import { RouterLink, useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiRefusal, loadFailureKind } from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { WatchedGroupMembers } from './WatchedGroupMembers';
import {
  emptyGroupForm,
  formToPayload,
  groupToForm,
  GROUP_NAME_MAX,
  hasGroupErrors,
  validateGroupForm,
  type GroupFormState,
} from './watched-groups-domain';
import {
  createWatchedGroup,
  getWatchedGroup,
  THRESHOLD_KINDS,
  updateWatchedGroup,
  WATCHED_GROUPS_KEY,
  type ThresholdKind,
} from './watched-groups-api';

const LIST_PATH = '/system-admin/watched-groups';

export function WatchedGroupFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<GroupFormState>(emptyGroupForm);
  const [initial, setInitial] = useState<GroupFormState>(emptyGroupForm);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<ApiRefusal | null>(null);

  const detail = useQuery({
    queryKey: ['watched-group', id],
    queryFn: () => getWatchedGroup(id ?? ''),
    enabled: isEdit,
  });

  // The form is filled once per group. The members change under it (each is its own write and
  // refetches the group), and a refetch must not overwrite what is being typed.
  const filledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!detail.data || filledFor.current === detail.data.id) return;
    filledFor.current = detail.data.id;
    const next = groupToForm(detail.data);
    setForm(next);
    setInitial(next);
  }, [detail.data]);

  const errors = validateGroupForm(form);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: (state: GroupFormState) =>
      isEdit
        ? updateWatchedGroup(id ?? '', formToPayload(state))
        : createWatchedGroup(formToPayload(state)),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: [WATCHED_GROUPS_KEY] });
      void queryClient.invalidateQueries({ queryKey: ['watched-group', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['watched-group-history', saved.id] });
      toast({
        variant: 'success',
        title: isEdit ? t('watched_groups.toast.saved') : t('watched_groups.toast.created'),
        text: saved.name,
        closeLabel: t('common.close'),
      });
      // A new group continues on its own page, where the members can be added.
      navigate(isEdit ? LIST_PATH : `${LIST_PATH}/${saved.id}/edit`);
    },
    onError: (err: unknown) => {
      setSubmitError(
        apiRefusal(
          err,
          isEdit ? 'watched_groups.error.save_failed' : 'watched_groups.error.create_failed',
        ),
      );
    },
  });

  function handleSave(): void {
    setSubmitError(null);
    setSubmitted(true);
    if (hasGroupErrors(errors)) return;
    save.mutate(form);
  }

  const noThreshold = form.thresholdKind === '';

  return (
    <FormPage
      breadcrumb={[
        { label: t('alerts.section_title'), href: '/system-admin/alerts' },
        { label: t('alerts.section.watched_groups'), href: LIST_PATH },
        { label: isEdit ? t('watched_groups.edit.crumb') : t('watched_groups.new.crumb') },
      ]}
      title={isEdit ? t('watched_groups.edit.title') : t('watched_groups.new.title')}
      lede={t('watched_groups.form.lede')}
      saveLabel={isEdit ? t('watched_groups.action.save') : t('watched_groups.action.create')}
      isSaving={save.isPending}
      isDirty={isDirty}
      isLoading={detail.isLoading}
      loadError={isEdit && detail.isError ? loadFailureKind(detail.error) : null}
      refusal={submitError}
      onSave={handleSave}
      onCancel={() => navigate(LIST_PATH)}
    >
      <FormSection
        title={t('watched_groups.section.identity')}
        lede={t('watched_groups.section.identity_lede')}
      >
        <FormField
          label={t('watched_groups.field.name')}
          required
          error={submitted && errors.name ? t(errors.name, { max: GROUP_NAME_MAX }) : undefined}
        >
          <MudTextInput
            size="md"
            maxLength={GROUP_NAME_MAX}
            value={form.name}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, name: e.detail.value })
            }
          />
        </FormField>
        <FormField label={t('watched_groups.field.description')}>
          <MudTextarea
            rows={3}
            resize="vertical"
            value={form.description}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              setForm({ ...form, description: e.detail.value })
            }
          />
        </FormField>
        <MudSwitch
          checked={form.active}
          onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
            setForm({ ...form, active: e.detail.checked })
          }
        >
          <span slot="label" className="simsm-alerts__switch-label">
            {t('watched_groups.field.active')}
            <span className="simsm-alerts__switch-hint">
              {t('watched_groups.field.active_hint')}
            </span>
          </span>
        </MudSwitch>
      </FormSection>

      <FormSection
        title={t('watched_groups.section.threshold')}
        lede={t('watched_groups.section.threshold_lede')}
      >
        <FormRow columns={2}>
          <FormField label={t('watched_groups.field.threshold_kind')}>
            <MudSelect
              size="medium"
              value={form.thresholdKind}
              onMudChange={(e: CustomEvent<{ value: string }>) => {
                const kind = e.detail.value as ThresholdKind | '';
                setForm({
                  ...form,
                  thresholdKind: kind,
                  thresholdValue: kind === '' ? '' : form.thresholdValue,
                });
              }}
            >
              <option value="">{t('watched_groups.threshold.none')}</option>
              {THRESHOLD_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`watched_groups.threshold.kind.${kind}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
          <FormField
            label={t('watched_groups.field.threshold_value')}
            required={!noThreshold}
            {...(form.thresholdKind === ''
              ? {}
              : { hint: t(`watched_groups.field.threshold_value_hint.${form.thresholdKind}`) })}
            error={submitted && errors.thresholdValue ? t(errors.thresholdValue) : undefined}
          >
            <MudTextInput
              size="md"
              inputMode="decimal"
              disabled={noThreshold}
              value={form.thresholdValue}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, thresholdValue: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
        {noThreshold ? (
          <MudInfoBox emphasis="subtle" variant="info">
            {t('watched_groups.threshold.none_note')}
          </MudInfoBox>
        ) : null}
      </FormSection>

      <FormSection
        title={t('watched_groups.section.members')}
        lede={t('watched_groups.section.members_lede')}
      >
        {isEdit && detail.data ? (
          <>
            <WatchedGroupMembers group={detail.data} />
            <RouterLink to={`${LIST_PATH}/${detail.data.id}/history`} size="sm">
              {t('watched_groups.action.history')}
            </RouterLink>
          </>
        ) : (
          <MudInfoBox emphasis="subtle" variant="info">
            {t('watched_groups.members.save_first')}
          </MudInfoBox>
        )}
      </FormSection>
    </FormPage>
  );
}
